<script lang="ts">
	import type { Readable } from 'svelte/store';
	import type { I18Next } from '$lib/IONOS/i18next.d.ts';
	import { onMount } from 'svelte';
	import Dialog from '$lib/IONOS/components/common/Dialog.svelte';
	import DialogHeader from '$lib/IONOS/components/common/DialogHeader.svelte';
	import Touch from '$lib/IONOS/components/icons/Touch.svelte';
	import Share from '$lib/IONOS/components/icons/Share.svelte';
	import Button from '$lib/IONOS/components/common/Button.svelte';
	import { ButtonType } from '$lib/IONOS/components/common/buttons';
	import { getContext } from 'svelte';
	import {
		dismissPWAPrompt,
		shouldShowPWAPrompt,
		triggerPWAInstall,
		isIOSDevice,
		isSafari
	} from '$lib/IONOS/services/pwa';
	import { config } from '$lib/stores';
	import {
		deferredPrompt,
		isPWAInstallable,
		clearDeferredPrompt,
	} from '$lib/IONOS/stores/pwa-prompt';
	import {
		addNotification,
		removeNotification,
		type Notification,
		NotificationType
	} from "$lib/IONOS/stores/notifications";

	const i18n = getContext<Readable<I18Next>>('i18n');

	let show = false;

	const showIOSInstructions = isIOSDevice() && isSafari();

	const handleInstall = async () => {
		if ($deferredPrompt) {
			const accepted = await triggerPWAInstall($deferredPrompt);
			if (accepted) {
				clearDeferredPrompt();
			}
		}

		show = false;
		// Called within the component via import of service function
		removeNotification("pwa");
	};

	const handleLater = () => {
		show = false;
	};

	const handlePWAInstall = async () => {
		if (showIOSInstructions) {
			show = true;
		} else if ($deferredPrompt) {
			const accepted = await triggerPWAInstall($deferredPrompt);
			if (accepted) {
				clearDeferredPrompt();
				removeNotification("pwa");
			}
		}
	};

	// Handler for the notification banner
	const dismissHandler = (event: CustomEvent) => {
		dismissPWAPrompt();
		removeNotification("pwa");
	};

	onMount(() => {
		if ("serviceWorker" in navigator) {
			navigator.serviceWorker.ready.then(() => {
				if (!shouldShowPWAPrompt($deferredPrompt)) {
					return;
				}

				const notification = {
					subscribe: (fn: (notification: Notification) => void) => {
						return i18n.subscribe(() => {
							fn({
								id: "pwa",
								type: NotificationType.PWA_INSTALL,
								title: $i18n.t('Install {{productName}}', { productName: $config?.name ?? '' }),
								message: $i18n.t('For quick and easy access, you can now install {{productName}} like an app!', { productName: $config?.name ?? '' }),
								actions: [{
									label: $i18n.t('Install'),
									handler: () => {
										handlePWAInstall();
									}
								}],
								dismiss: dismissHandler,
								dismissible: true,
							});
						});
					}
				};

				addNotification(notification);
			});
		}
	});

	$: if (!$isPWAInstallable && !$deferredPrompt) {
		show = false;
		removeNotification("pwa");
	}
</script>

<Dialog bind:show on:close={handleLater}
	dialogId="pwa-install"
	class="p-[30px] items-end md:items-center pb-[25px] px-[25px] max-md:mb-0 {show ? 'max-md:translate-y-[-5dvh]' : 'max-md:translate-y-[50dvh]'}"
	animationDuration={150}
	mobileCover={false}
	externalClose={true}
>
	<DialogHeader closable={false} slot="header" />
	<div slot="content" class="w-full ">
		{#if showIOSInstructions}
			<div class="text-center mb-6">
				<div class="w-16 h-16 bg-modal-icon-bg rounded-full flex items-center justify-center mx-auto mb-4">
					<Touch className="w-8 h-8 text-modal-icon-stroke" />
				</div>
				<h3 class="text-xl font-semibold text-modal-text mb-2">
					{$i18n.t('Install {{productName}}', { productName: $config?.name ?? '' })}
				</h3>
				<p class="text-modal-text mb-6">
					{$i18n.t('For quick and easy access, you can now install {{productName}} like an app!', { productName: $config?.name ?? '' })}
				</p>
			</div>

			<div class="flex flex-col justify-center space-y-4 mb-6">
				<div class="flex items-start space-x-3">
					<div class="flex-shrink-0 w-6 h-6 bg-content-marker-bg rounded-full flex items-center justify-center">
						<span class="text-content-marker-stroke text-sm font-medium">1</span>
					</div>
					<div class="flex-1">
						<p class="text-modal-text">
							{$i18n.t('Tap the Share button')}
							<Share className="inline w-4 h-4 mx-1" />
						</p>
					</div>
				</div>

				<div class="flex items-start space-x-3">
					<div class="flex-shrink-0 w-6 h-6 bg-content-marker-bg rounded-full flex items-center justify-center">
						<span class="text-content-marker-stroke text-sm font-medium">2</span>
					</div>
					<div class="flex-1 flex flex-row gap-1">
						<p class="text-modal-text">
							{$i18n.t('Select')}
						</p>
						<p class="font-semibold">{$i18n.t('Add to Home Screen')}</p>
					</div>
				</div>
			</div>
		{:else}
			<div class="text-center mb-6">
				<div class="w-16 h-16 bg-modal-icon-bg rounded-full flex items-center justify-center mx-auto mb-4">
					<Touch className="w-8 h-8 text-modal-icon-stroke" />
				</div>
				<h3 class="text-xl font-semibold text-modal-text mb-2">
					{$i18n.t('Install {{productName}}', { productName: $config?.name ?? '' })}
				</h3>
				<p class="text-modal-text mb-6">
					{$i18n.t('For quick and easy access, you can now install {{productName}} like an app!', { productName: $config?.name ?? '' })}
				</p>
			</div>

			<div class="flex justify-center space-x-3">
				<Button
					on:click={handleInstall}
					type={ButtonType.primary}
					className="transition-colors"
				>
					{$i18n.t('Install {{productName}}', { productName: $config?.name ?? '' })}
				</Button>
			</div>
		{/if}
	</div>
</Dialog>
